/**
 * SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/layout/PlatformAdminLayout.tsx
 * PURPOSE: Platform Admin shared sidebar layout — role-based navigation for 5 PA modules
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-03-28
 * COMPLIANCE: SDLC 6.3.1 + NQH Brand Design System + SPEC-0416 + DD-0002 RBAC
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Platform Admin navigation — sidebar for 5 PA modules with RBAC visibility
 * - Impact: P0 — PA-006 Go-Live blocker (no shared navigation)
 * - Stakeholders: Platform Admins (6 roles), CTO, Remote Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Layout Component)
 * - Pattern: Header + Sidebar + Content area, responsive mobile bottom tab
 * - Dependencies: React Router (Outlet), PlatformAdminContext, CSS Modules, NQH tokens
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/SPEC-0416-platform-admin-sidebar-layout.md
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - RBAC: backend/app/models/rbac_v2.py (PlatformAdminRole enum)
 * - Reference: frontend/src/layouts/TenantAdminLayout.tsx (pattern source)
 * - Sprint: SPRINT-075 S75-011 (Track 3)
 */

import React from "react";
import { useNavigate, useLocation, Outlet, Navigate } from "react-router-dom";
import { usePlatformAdminAuth } from "../../contexts/PlatformAdminContext";
import styles from "./PlatformAdminLayout.module.css";

// ─── Menu Configuration (SPEC-0416 §2.2) ────────────────────────────────────

interface MenuItem {
  id: string;
  icon: string;
  label: string;
  path: string;
  roles: string[];
}

const PA_MENU_ITEMS: MenuItem[] = [
  {
    id: "dashboard",
    icon: "📊",
    label: "Bảng điều khiển",
    path: "/platform-admin/dashboard",
    roles: ["*"],
  },
  {
    id: "tenants",
    icon: "🏢",
    label: "Quản lý Tenant",
    path: "/platform-admin/tenants",
    roles: ["platform_super_admin", "platform_operator", "platform_support_l2"],
  },
  {
    id: "admins",
    icon: "👥",
    label: "Quản lý Admin",
    path: "/platform-admin/admins",
    roles: ["platform_super_admin"],
  },
  {
    id: "audit-logs",
    icon: "📋",
    label: "Nhật ký kiểm toán",
    path: "/platform-admin/audit-logs",
    roles: ["platform_super_admin", "platform_security"],
  },
  {
    id: "ip-whitelist",
    icon: "🔒",
    label: "IP Whitelist",
    path: "/platform-admin/ip-whitelist",
    roles: ["platform_super_admin", "platform_security"],
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

const PlatformAdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { platformAdmin, isAuthenticated, isInitialized, logout } =
    usePlatformAdminAuth();

  // RBAC Guard — redirect if not authenticated PA
  if (isInitialized && !isAuthenticated) {
    return <Navigate to="/platform-admin/login" replace />;
  }

  // Wait for initialization (prevent flash)
  if (!isInitialized) {
    return null;
  }

  const adminRole = platformAdmin.admin?.role || "";

  // SPEC-0416 §2.3: Filter menu items by admin role
  const visibleItems = PA_MENU_ITEMS.filter(
    (item) => item.roles.includes("*") || item.roles.includes(adminRole)
  );

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const handleLogout = () => {
    logout();
    navigate("/platform-admin/login");
  };

  return (
    <div className={styles.layout}>
      {/* Header — SPEC-0416 §2.1 */}
      <header className={styles.header}>
        <h1 className={styles.headerTitle}>Quản trị Nền tảng</h1>
        <div className={styles.headerRight}>
          <span className={styles.headerAdminName}>
            {platformAdmin.admin?.full_name || platformAdmin.admin?.username || "Admin"}
          </span>
          <button
            className={styles.headerLogoutBtn}
            onClick={handleLogout}
          >
            Đăng xuất
          </button>
        </div>
      </header>

      <div className={styles.body}>
        {/* Sidebar — SPEC-0416 §2.2 + §2.3 Role Visibility */}
        <nav className={styles.sidebar}>
          {visibleItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={isActive(item.path) ? styles.activeItem : styles.menuItem}
              title={item.label}
            >
              <span className={styles.menuIcon}>{item.icon}</span>
              <span className={styles.menuLabel}>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Content — renders child route via Outlet */}
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default PlatformAdminLayout;
